import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { ApiError, getUser, resetUserTwoFactor, setUserPassword, updateUser, type Role, type User, type UserUpdate } from "@/api/client";
import { useSession } from "@/auth/session";
import { useConfirmFocus } from "@/components/confirmFocus";
import { formatDate } from "@/components/format";
import { Field, inputClass, serverProblems, submitClass } from "@/components/form";
import { DetailField, EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { ConflictBanner } from "@/features/users/conflicts";
import { passwordProblem } from "@/features/users/password";
import { RoleCheckboxes, roleNames, useRoleChoices } from "@/features/users/UserList";

const detailApi = getRouteApi("/staff/users/$userId");

export function UserDetailPage() {
  const { userId } = detailApi.useParams();
  const user = useQuery({ queryKey: ["user", userId], queryFn: () => getUser(userId) });

  // 404 is an unknown id, 422 a malformed one; both mean the link points at nothing.
  if (user.error instanceof ApiError && [404, 422].includes(user.error.status)) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="User not found" detail="No user matches this link." />
        <Link to="/users" className="text-sm font-medium text-zinc-950 underline">
          Back to users
        </Link>
      </section>
    );
  }
  if (!user.data) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="User" detail={userId} />
        <ErrorBanner error={user.error} />
        {user.isLoading ? <EmptyState title="Loading user" detail="Fetching the user." /> : null}
      </section>
    );
  }
  return <UserView user={user.data} />;
}

function UserView({ user }: { user: User }) {
  const { me, can } = useSession();
  const queryClient = useQueryClient();
  const isMe = me?.user.id === user.id;
  const manage = can("user.manage");

  const store = async (next: User) => {
    // A refetch begun before the save would otherwise land after it with the old values.
    await queryClient.cancelQueries({ queryKey: ["user", user.id] });
    queryClient.setQueryData(["user", user.id], next);
    await queryClient.invalidateQueries({ queryKey: ["users"] });
    await queryClient.invalidateQueries({ queryKey: ["roles"] });
  };

  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title={user.full_name}
        detail={user.email}
        action={
          can("audit.read") ? (
            <Link to="/audit" search={{ actor: user.id }} className="text-sm font-medium text-zinc-950 underline">
              Audit trail
            </Link>
          ) : null
        }
      />
      <div className="rounded-lg border border-zinc-200 bg-white p-4">
        <dl className="grid gap-3 text-sm md:grid-cols-4">
          <DetailField label="Email" value={user.email} />
          <DetailField label="Status" value={user.is_active ? "Active" : "Inactive"} />
          <DetailField label="Last login" value={user.last_login_at ? formatDate(user.last_login_at) : "Never"} />
          <DetailField label="Roles" value={roleNames(user)} />
        </dl>
      </div>
      {manage ? (
        <>
          <EditUser user={user} isMe={isMe} onSaved={store} />
          <AccountStatus user={user} isMe={isMe} onSaved={store} />
          <SetPassword user={user} isMe={isMe} />
          <TrustedDevices user={user} />
        </>
      ) : null}
    </section>
  );
}

const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id));

function EditUser({ user, isMe, onSaved }: { user: User; isMe: boolean; onSaved: (next: User) => Promise<void> }) {
  const { refresh } = useSession();
  const { roles, error: rolesError } = useRoleChoices();
  const savedRoleIds = (user.roles ?? []).map((role) => role.id).filter((id): id is string => Boolean(id));
  const [fullName, setFullName] = useState(user.full_name);
  const [roleIds, setRoleIds] = useState(savedRoleIds);
  const [saved, setSaved] = useState(false);

  // A refetch with another admin's change replaces the form; our own save lands on the same values.
  // Sorted: the server orders roles by name, so a rename elsewhere mustn't reset the form.
  const signature = JSON.stringify([user.full_name, [...savedRoleIds].sort()]);
  const [seen, setSeen] = useState(signature);
  if (signature !== seen) {
    setSeen(signature);
    setFullName(user.full_name);
    setRoleIds(savedRoleIds);
  }

  const body: UserUpdate = {};
  if (fullName.trim() !== user.full_name) body.full_name = fullName.trim();
  // Without role.read the checkboxes aren't shown, so roles never change here.
  if (roles !== null && !sameSet(roleIds, savedRoleIds)) body.role_ids = orderedLike(roleIds, roles);

  const save = useMutation({
    mutationFn: (update: UserUpdate) => updateUser(user.id, update),
    onSuccess: async (next, update) => {
      setSaved(true);
      setFullName(next.full_name);
      setRoleIds((next.roles ?? []).map((role) => role.id).filter((id): id is string => Boolean(id)));
      await onSaved(next);
      // Your own roles decide your permissions, so the nav and guards must re-read them.
      if (isMe && update.role_ids) await refresh();
    },
  });
  const edited = () => {
    setSaved(false);
    if (save.error) save.reset();
  };
  const server = serverProblems(save.error, ["full_name", "role_ids"]);
  const nameProblem = fullName.trim() === "" ? "is required" : server.byField.full_name;
  const blocked = Boolean(nameProblem) || Object.keys(body).length === 0;

  return (
    <form
      noValidate
      aria-label="Edit user"
      className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!blocked && !save.isPending) save.mutate(body);
      }}
    >
      <h2 className="text-sm font-semibold">Profile and roles</h2>
      <Field label="Full name" problem={nameProblem}>
        {(props) => (
          <input
            {...props}
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value);
              edited();
            }}
            className={inputClass}
          />
        )}
      </Field>
      {rolesError ? (
        <p role="alert" className="text-sm text-red-700">
          Roles could not be loaded: {rolesError.message}. Reload before assigning roles.
        </p>
      ) : null}
      <RoleCheckboxes
        roles={roles}
        selected={roleIds}
        problem={server.byField.role_ids}
        onChange={(next) => {
          setRoleIds(next);
          edited();
        }}
        missingNote="You can't list roles (role.read), so this user's roles can't be changed here."
      />
      <ConflictBanner error={save.error} />
      <button type="submit" disabled={blocked || save.isPending} className={submitClass}>
        {save.isPending ? "Saving..." : "Save changes"}
      </button>
      {saved ? (
        <p role="status" className="text-sm font-medium text-emerald-700">
          Saved.
        </p>
      ) : null}
    </form>
  );
}

// Sent in the roles list's order, so the request doesn't depend on click order.
function orderedLike(ids: string[], roles: Role[]) {
  const known = roles.map((role) => role.id).filter((id) => ids.includes(id));
  return [...known, ...ids.filter((id) => !known.includes(id))];
}

function AccountStatus({ user, isMe, onSaved }: { user: User; isMe: boolean; onSaved: (next: User) => Promise<void> }) {
  const [confirming, setConfirming] = useState(false);
  const { trigger, confirm } = useConfirmFocus(confirming);
  const change = useMutation({
    mutationFn: (isActive: boolean) => updateUser(user.id, { is_active: isActive }),
    onSuccess: async (next) => {
      setConfirming(false);
      await onSaved(next);
    },
  });

  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Account status</h2>
          <p className="mt-1 text-xs text-zinc-500">
            {user.is_active ? "Active: can sign in." : "Inactive: can't sign in."}
          </p>
        </div>
        {!user.is_active ? (
          <ToolbarButton ref={trigger} disabled={change.isPending} onClick={() => change.mutate(true)}>
            Reactivate
          </ToolbarButton>
        ) : isMe ? (
          <p className="text-sm text-zinc-500">You can't deactivate your own account.</p>
        ) : (
          <ToolbarButton ref={trigger} tone="danger" disabled={confirming || change.isPending} onClick={() => setConfirming(true)}>
            Deactivate
          </ToolbarButton>
        )}
      </div>
      {confirming ? (
        <div role="group" aria-label="Confirm deactivation" className="flex flex-wrap items-center gap-3 rounded-md bg-zinc-50 p-3 text-sm">
          <p className="min-w-72 flex-1 text-zinc-700">
            Deactivate {user.full_name}? They won't be able to sign in until reactivated.
          </p>
          <ToolbarButton ref={confirm} tone="danger" disabled={change.isPending} onClick={() => change.mutate(false)}>
            {change.isPending ? "Deactivating..." : `Deactivate ${user.full_name}`}
          </ToolbarButton>
          <ToolbarButton disabled={change.isPending} onClick={() => setConfirming(false)}>
            Cancel
          </ToolbarButton>
        </div>
      ) : null}
      <ConflictBanner error={change.error} />
    </div>
  );
}

function SetPassword({ user, isMe }: { user: User; isMe: boolean }) {
  const { signOut } = useSession();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [confirming, setConfirming] = useState(false);
  const { trigger, confirm } = useConfirmFocus(confirming);

  const set = useMutation({
    mutationFn: (next: string) => setUserPassword(user.id, next),
    onSuccess: async () => {
      setConfirming(false);
      setPassword("");
      setRepeat("");
      if (isMe) {
        // The server has ended this session too; leave the way Sign out does.
        await signOut();
        await navigate({ to: "/login" });
      }
    },
    onError: () => setConfirming(false),
  });
  const edited = () => {
    setConfirming(false);
    if (set.error || set.isSuccess) set.reset();
  };

  const server = serverProblems(set.error, ["password"]);
  const problem = (password === "" ? undefined : passwordProblem(password)) ?? server.byField.password;
  const mismatch = repeat !== "" && repeat !== password ? "doesn't match" : undefined;
  const blocked = password === "" || Boolean(problem) || repeat !== password;

  return (
    <section aria-labelledby="set-password" className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div>
        <h2 id="set-password" className="text-sm font-semibold">
          Set password
        </h2>
        <p className="mt-1 text-xs text-zinc-500">12 to 1024 bytes. Setting a password ends every session the user has open.</p>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="New password" problem={problem}>
          {(props) => (
            <input
              {...props}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                edited();
              }}
              className={inputClass}
            />
          )}
        </Field>
        <Field label="Confirm password" problem={mismatch}>
          {(props) => (
            <input
              {...props}
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => {
                setRepeat(e.target.value);
                edited();
              }}
              className={inputClass}
            />
          )}
        </Field>
      </div>
      <ToolbarButton ref={trigger} disabled={blocked || confirming || set.isPending} onClick={() => setConfirming(true)}>
        Set password
      </ToolbarButton>
      {confirming ? (
        <div role="group" aria-label="Confirm password change" className="flex flex-wrap items-center gap-3 rounded-md bg-zinc-50 p-3 text-sm">
          <p className="min-w-72 flex-1 text-zinc-700">
            {isMe
              ? "This ends all your sessions, this one included: you'll be signed out."
              : `This ends all of ${user.full_name}'s sessions; they sign in again with the new password.`}
          </p>
          <ToolbarButton ref={confirm} disabled={set.isPending} onClick={() => set.mutate(password)}>
            {set.isPending ? "Setting..." : isMe ? "Set password and sign out" : `Set password for ${user.full_name}`}
          </ToolbarButton>
          <ToolbarButton disabled={set.isPending} onClick={() => setConfirming(false)}>
            Cancel
          </ToolbarButton>
        </div>
      ) : null}
      <ErrorBanner error={set.error} />
      {set.isSuccess && !isMe ? (
        <p role="status" className="text-sm font-medium text-emerald-700">
          Password set. {user.full_name}'s sessions have ended.
        </p>
      ) : null}
    </section>
  );
}

function TrustedDevices({ user }: { user: User }) {
  const [confirming, setConfirming] = useState(false);
  const { trigger, confirm } = useConfirmFocus(confirming);
  const reset = useMutation({
    mutationFn: () => resetUserTwoFactor(user.id),
    onSettled: () => setConfirming(false),
  });
  const ask = () => {
    if (reset.error || reset.isSuccess) reset.reset();
    setConfirming(true);
  };

  return (
    <section aria-labelledby="trusted-devices" className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id="trusted-devices" className="text-sm font-semibold">
            Trusted devices
          </h2>
          <p className="mt-1 text-xs text-zinc-500">Forget this person's trusted devices so their next sign-in needs a code.</p>
        </div>
        <ToolbarButton ref={trigger} tone="danger" disabled={confirming || reset.isPending} onClick={ask}>
          Forget all devices
        </ToolbarButton>
      </div>
      {confirming ? (
        <div role="group" aria-label="Confirm forget devices" className="flex flex-wrap items-center gap-3 rounded-md bg-zinc-50 p-3 text-sm">
          <p className="min-w-72 flex-1 text-zinc-700">Forget every trusted device of {user.full_name}?</p>
          <ToolbarButton ref={confirm} tone="danger" disabled={reset.isPending} onClick={() => reset.mutate()}>
            {reset.isPending ? "Forgetting..." : "Forget devices"}
          </ToolbarButton>
          <ToolbarButton disabled={reset.isPending} onClick={() => setConfirming(false)}>
            Cancel
          </ToolbarButton>
        </div>
      ) : null}
      <ErrorBanner error={reset.error} />
      {reset.isSuccess ? (
        <p role="status" className="text-sm font-medium text-emerald-700">
          Devices forgotten. Their next sign-in needs a code.
        </p>
      ) : null}
    </section>
  );
}
